/**
 * One tab of the project in view, in its frame.
 *
 * The frame is the shell's: it is drawn whether the tab's code has arrived or
 * not, so moving between tabs keeps the project's name and tab bar in place and
 * only the body below them waits. What a tab puts in the frame is its own
 * module (`tab-*.ts`), each a chunk fetched the first time that tab is opened.
 */

import type { TemplateInstance } from 'hydrogen/runtime';

import type { SurfaceSnapshot } from '../generated/surface.js';
import type { ChangeDetailView } from './inspector.js';
import { requestedInsightsShow } from './insights-show.js';
import type { LensId } from './lenses/catalog.js';
import type { ProjectRoute } from './nav.js';
import { NotFoundBody } from './not-found.js';
import type { CodeView } from './project-code.js';
import { ProjectFrame } from './project-frame.js';
import type { SurfaceConnectionState } from './surface-connection.js';

/** What a project page is drawn from beyond the projection: drill-downs and live mounts. */
export type ProjectPageParts = {
  route: ProjectRoute;
  snapshot: SurfaceSnapshot;
  connection: SurfaceConnectionState;
  path: string;
  search: string;
  detail: ChangeDetailView;
  code: CodeView;
  /** Insights › Frontier, mounted by the app. */
  manifold: () => TemplateInstance;
  /** What fetching a lens's code says, or nothing once it is here. */
  lensNotice: (id: LensId) => TemplateInstance;
};

/** What one tab draws inside the frame. */
export type ProjectTabBody = (parts: ProjectPageParts) => TemplateInstance;

/** The shape every `tab-*.ts` chunk has: the body of one tab. */
export type ProjectTabModule = { projectTabBody: ProjectTabBody };

/**
 * Bodies that title themselves: a list or a change has its own page heading, so
 * the project name above it is not the root. Overview, Work, Checks, Settings
 * and the Insights lenses are sections of the project the name heads.
 */
function titlesItself(route: ProjectRoute, search: string): boolean {
  if (route.tab === 'code' || route.tab === 'changes') return true;
  if (route.tab !== 'insights') return false;
  const show = requestedInsightsShow(search);
  return show === 'graph' || show === 'frontier';
}

/**
 * One tab of the project in view, in its frame; a snapshot of another project is not this page.
 * `body` is called only for this project's page. `bodyTitled` is for a body that is not the
 * tab's own -- the loading card, which carries a heading of its own.
 */
export function ProjectPage(
  parts: ProjectPageParts,
  body: () => TemplateInstance,
  bodyTitled: boolean = titlesItself(parts.route, parts.search),
): TemplateInstance {
  // The snapshot is read for the project the address names; one of any other project is not this page.
  if (parts.route.project !== parts.snapshot.product) return NotFoundBody({ path: parts.path, signedIn: true });
  return ProjectFrame({
    project: parts.route.project,
    tab: parts.route.tab,
    snapshot: parts.snapshot,
    connection: parts.connection,
    body: body(),
    bodyTitled,
  });
}
